import {
    authLogout,
    authLogoutPath,
    ConfigurationContext,
    getAuthParams,
    getLoginUrl,
    getRegisterUrl,
    getSubscribeUrl,
    isArticle,
    IsHydrating,
    isServerEnvironment,
    LogoutEvent,
} from '@news-mono/web-common'
import React, { useContext } from 'react'
import { useDispatch } from 'react-redux'
import { useSectionMetaInfo } from '../../__helpers/use-section-meta'
import { BreachScreenContext } from '../breach/BreachContextProvider'
interface SubscribeClickOverrides {
    offerCode?: string
    packagePath?: string
    callToAction?: string
}

export type OnSubscribeEventHandler = (
    overrides?: SubscribeClickOverrides,
) => void

export type AuthProviderOnEvent = (event: LogoutEvent) => void
interface AuthenticationProviderContextValues {
    onLoginClick(): void
    onRegisterClick(): void
    onLogoutClick(onEvent: AuthProviderOnEvent): void
    onSubscribeClick: OnSubscribeEventHandler
    getSubscribeLink: (overrides?: SubscribeClickOverrides) => string
}

export const AuthenticationProviderContext =
    React.createContext<AuthenticationProviderContextValues | undefined>(
        undefined,
    )

export function useAuthActions() {
    const values = useContext(AuthenticationProviderContext)
    if (!values) {
        throw new Error(
            'Ensure <AuthenticationProvider /> is rendered above the component using this hook',
        )
    }

    return values
}

export const AuthenticationProvider: React.FC<React.PropsWithChildren<{}>> = ({
    children,
}) => {
    const dispatch = useDispatch()
    const config = useContext(ConfigurationContext)
    const sectionMeta = useSectionMetaInfo()
    const isHydrating = React.useContext(IsHydrating)
    const breachScreenContext = React.useContext(BreachScreenContext)
    const context = breachScreenContext.context

    const hostname = sectionMeta.Hostname
        ? `https://${sectionMeta.Hostname}`
        : config.publicUrl
    const pageUrl =
        isServerEnvironment() || isHydrating
            ? hostname
            : `${hostname}${window.location.pathname}`

    const onLoginClick = () => {
        const loginUrl = getLoginUrl()
        window.location.href = loginUrl
    }

    const onLogoutClick = async (onEvent: AuthProviderOnEvent) => {
        dispatch(
            authLogout({
                onEvent,
            }),
        )
        window.location.href = authLogoutPath
    }

    function getSubscribeLink(overrides: SubscribeClickOverrides | undefined) {
        const auth = getAuthParams({ sectionMeta })
        const article =
            context?.publication && isArticle(context?.publication)
                ? context.publication
                : undefined

        const subscribeLink = getSubscribeUrl({
            config,
            auth,
            pageUrl,
            article,
            packagePath: overrides?.packagePath || context?.packagePath,
            source: sectionMeta.Hostname,
            productName: sectionMeta.Title,
            campaign: context?.breachCreativeName,
            callToAction: overrides?.callToAction,
            additionalParams: {
                utm_term: context?.breachDeal,
                utm_content: context?.publication?.id,
            },
            offerCode: overrides?.offerCode || context?.offerCode,
            returningUser: context?.returningUser,
            requiredAccessLevel: context?.requiredAccessLevel,
        })
        return subscribeLink
    }

    const onSubscribeClick: OnSubscribeEventHandler = (overrides) => {
        const subscribeLink = getSubscribeLink(overrides)

        window.location.href = subscribeLink
    }

    const onRegisterClick = () => {
        const registerUrl = getRegisterUrl()
        window.location.href = registerUrl
    }

    const values = {
        onLoginClick,
        onRegisterClick,
        onLogoutClick,
        onSubscribeClick,
        getSubscribeLink,
    }

    return (
        <AuthenticationProviderContext.Provider value={values}>
            {children}
        </AuthenticationProviderContext.Provider>
    )
}
